import {
  Component,
  computed,
  inject,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  Signal,
} from '@angular/core';
import { WorkoutMode } from '@kinomap-dev/ng-ui-lib/core';
import { MinMaxStep, SectionChange, SectionModelType } from '../../types/section.type';
import { Duration } from '../../types/duration.type';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { ColorService } from '../../services/color.service';
import { ReactiveFormsModule } from '@angular/forms';
import { SectionsEditorService } from '../../services/sections-editor.service';
import { IconComponent } from '@kinomap-dev/ng-ui-lib/basics';
import { durationToSeconds, secondsToDuration } from '../../utils/duration.utils';
import { Keydown } from '../../directives/keydown';
import {
  DEFAULT_SECTION_CADENCE,
  MIN_SECTION_DURATION_SECONDS,
  SECTION_CADENCE_MIN_MAX_STEP,
} from '../../configs/sections-editor/default-section.config';
import { maxValueLength } from '../../utils/sections-editor.utils';

import { TranslateModule } from '@ngx-translate/core';
@Component({
  selector: 'app-section-row',
  imports: [TranslateModule, IconComponent, CdkDrag, CdkDragHandle, ReactiveFormsModule],
  templateUrl: './section-row.html',
  styleUrl: './section-row.scss',
})
export class SectionRow {
  protected colorService: ColorService = inject(ColorService);
  public sectionEditorService: SectionsEditorService = inject(SectionsEditorService);
  private keydown: Keydown = inject(Keydown);
  section: InputSignal<SectionModelType> = input.required<SectionModelType>();
  mode: InputSignal<WorkoutMode | undefined> = input.required<WorkoutMode | undefined>();
  duplicate: OutputEmitterRef<SectionModelType> = output<SectionModelType>();
  delete: OutputEmitterRef<void> = output<void>();
  // Pas `change` : l'event DOM `change` des inputs remonte jusqu'a l'hote et serait capte aussi.
  sectionChange: OutputEmitterRef<SectionChange> = output<SectionChange>();
  dragBoundaryClass: InputSignal<string | undefined> = input<string>();
  isSelected: Signal<boolean> = computed(() =>
    this.sectionEditorService.selectedSectionsIds().includes(this.section().id),
  );
  protected readonly cadenceBounds: Readonly<MinMaxStep> = SECTION_CADENCE_MIN_MAX_STEP;
  protected readonly cadenceWidth: number = maxValueLength(SECTION_CADENCE_MIN_MAX_STEP);
  isHovered: Signal<boolean> = computed(
    () => this.sectionEditorService.hoveredSectionId() === this.section().id,
  );

  protected fieldWidth(type: WorkoutMode): number {
    return maxValueLength(this.sectionEditorService.fieldBounds(type));
  }

  get sectionDatas(): { type: WorkoutMode; value: number }[] {
    return this.sectionEditorService.sectionDatas(this.section(), this.mode());
  }

  protected onRowClick(event: MouseEvent): void {
    if (this.shouldKeepSelection(event)) return;
    this.sectionEditorService.selectSection(this.section().id, {
      toggle: this.keydown.isCtrlOrMetaPressed(),
      range: this.keydown.isShiftPressed(),
    });
  }

  /** Sur Mac, Ctrl + clic declenche un clic droit et jamais de `click`. */
  protected onRowContextMenu(event: MouseEvent): void {
    if (!event.ctrlKey || this.shouldKeepSelection(event)) return;
    event.preventDefault();
    this.sectionEditorService.selectSection(this.section().id, {
      toggle: true,
      range: this.keydown.isShiftPressed(),
    });
  }

  private shouldKeepSelection(event: MouseEvent): boolean {
    const target: HTMLElement = event.target as HTMLElement;
    if (target.closest('button')) return true;
    return !!target.closest('input') && this.isSelected();
  }

  protected onRowMouseDown(event: MouseEvent): void {
    if (event.shiftKey && !(event.target as HTMLElement).closest('input')) event.preventDefault();
  }

  onDataChange(type: WorkoutMode, value: string) {
    if (Number.isNaN(parseInt(value))) throw new Error('data change is not a number');
    const current: number = this.sectionEditorService.sectionDataValue(
      this.section(),
      this.mode(),
      type,
    );
    this.sectionChange.emit({ field: 'data', type, delta: parseInt(value) - current });
  }

  protected readonly minDurationSeconds: number = MIN_SECTION_DURATION_SECONDS;

  protected onTimeChange(field: 'minutes' | 'seconds', input: HTMLInputElement): void {
    const current: Duration = this.section().duration;
    const parsed: number = parseInt(input.value);
    const value: number = Number.isNaN(parsed)
      ? current[field]
      : field === 'minutes'
        ? Math.max(0, parsed)
        : parsed;
    const total: number = Math.max(
      MIN_SECTION_DURATION_SECONDS,
      durationToSeconds({ ...current, [field]: value }),
    );
    input.value = String(secondsToDuration(total)[field]).padStart(2, '0');
    const delta: number = total - durationToSeconds(current);
    if (delta) this.sectionChange.emit({ field: 'duration', delta });
  }

  protected addCadence(): void {
    this.sectionChange.emit({ field: 'cadence', delta: 0 });
  }

  protected onCadenceChange(value: string): void {
    if (!parseInt(value)) return;
    const current: number = this.section().cadence ?? DEFAULT_SECTION_CADENCE;
    this.sectionChange.emit({ field: 'cadence', delta: parseInt(value) - current });
  }

  protected onCadenceBlur(value: string): void {
    if (!parseInt(value)) this.sectionChange.emit({ field: 'cadence', delta: undefined });
  }
}
